function createRule(md, ruleName, styleProperty, regex, styleExtractor) {
  function tokenize(state, silent) {
    // Match opening tag
    const openMatch = state.src.slice(state.pos).match(regex.open);
    if (openMatch) {
      if (!silent) {
        const token = state.push('text', '', 0);
        token.content = openMatch[0];

        state.delimiters.push({
          marker: ruleName, // Marker scoped to this rule so rules don't cross-match each other's delimiters
          length: 0,
          token: state.tokens.length - 1,
          end: -1, // Will be updated in postProcess
          open: true,
          close: false,
          styleValue: styleExtractor(openMatch), // Extract the style value
        });
      }
      state.pos += openMatch[0].length;
      return true;
    }

    // Match closing tag
    const closeMatch = state.src.slice(state.pos).match(regex.close);
    if (closeMatch) {
      if (!silent) {
        const token = state.push('text', '', 0);
        token.content = closeMatch[0];

        state.delimiters.push({
          marker: ruleName, // Marker scoped to this rule so rules don't cross-match each other's delimiters
          length: 0,
          token: state.tokens.length - 1,
          end: -1, // Will be updated in postProcess
          open: false,
          close: true,
        });
      }
      state.pos += closeMatch[0].length;
      return true;
    }

    return false;
  }

  function postProcess(state, delimiters) {
    const max = delimiters.length;
    const stack = []; // Stack to track unmatched opening delimiters

    for (let i = 0; i < max; i++) {
      const delim = delimiters[i];

      if (delim.marker !== ruleName) {
        continue; // Skip delimiters that belong to a different custom tag rule
      }

      if (delim.open) {
        stack.push(i); // Push the index of the opening delimiter onto the stack
      } else if (delim.close) {
        if (stack.length === 0) {
          continue; // No matching opening delimiter
        }

        const openIndex = stack.pop(); // Get the matching opening delimiter
        const openDelim = delimiters[openIndex];

        // Update the `end` properties of the matching delimiters
        openDelim.end = i;
        delim.end = openIndex;

        // Replace the opening token
        const tokenOpen = state.tokens[openDelim.token];
        tokenOpen.type = `${ruleName}_open`;
        tokenOpen.tag = 'span';
        tokenOpen.nesting = 1;

        // Ensure styles are correctly mapped to their properties
        const existingStyle = tokenOpen.attrs?.find(attr => attr[0] === 'style');
        const newStyle = `${styleProperty}: ${openDelim.styleValue}`;
        if (existingStyle) {
          const stylesMap = Object.fromEntries(
            existingStyle[1].split(';').map(style => style.trim().split(':').map(s => s.trim()))
          );
          stylesMap[styleProperty] = openDelim.styleValue; // Update or add the new style
          existingStyle[1] = Object.entries(stylesMap).map(([key, value]) => `${key}: ${value}`).join('; ');
        } else {
          tokenOpen.attrs = [['style', newStyle]];
        }

        tokenOpen.markup = `[${ruleName}=...]`;
        tokenOpen.content = '';

        // Replace the closing token
        const tokenClose = state.tokens[delim.token];
        tokenClose.type = `${ruleName}_close`;
        tokenClose.tag = 'span';
        tokenClose.nesting = -1;
        tokenClose.markup = `[/${ruleName}]`;
        tokenClose.content = '';
      }
    }
  }

  md.inline.ruler.before('emphasis', ruleName, tokenize);
  md.inline.ruler2.before('emphasis', ruleName, function (state) {
    const tokens_meta = state.tokens_meta;
    const max = state.tokens_meta.length;

    postProcess(state, state.delimiters);

    for (let curr = 0; curr < max; curr++) {
      if (tokens_meta[curr] && tokens_meta[curr].delimiters) {
        postProcess(state, tokens_meta[curr].delimiters);
      }
    }
  });

  md.renderer.rules[`${ruleName}_open`] = (tokens, idx) => {
    const style = tokens[idx].attrs.find(attr => attr[0] === 'style')[1];
    return `<span style="${style}">`;
  };

  md.renderer.rules[`${ruleName}_close`] = () => {
    return '</span>';
  };
}

export function createFontSizeRule(md) {
  createRule(
    md,
    'fontSize',
    'font-size',
    {
      open: /^\[fontSize=(\d+)\]/,
      close: /^\[\/fontSize\]/,
    },
    match => `${match[1]}px` // Extract font size and append "px"
  );
}

export function createTextColorRule(md) {
  createRule(
    md,
    'textColor',
    'color',
    {
      open: /^\[textColor=([^\]]+)\]/,
      close: /^\[\/textColor\]/,
    },
    match => match[1] // Extract the color value directly
  );
}

export function createFontFamilyRule(md) {
  createRule(
    md,
    'fontFamily',
    'font-family',
    {
      open: /^\[fontFamily=([^\]]+)\]/,
      close: /^\[\/fontFamily\]/,
    },
    match => match[1] // Extract font family directly
  );
}

function createAlignmentRule(md, ruleName, extraStyles = []) {
  const baseName = ruleName.replace(/(?:RTL|LTR)$/, '');
  const alignment = baseName.replace('align', '').toLowerCase();

  md.block.ruler.before(
    'paragraph',
    ruleName,
    (state, startLine, endLine, silent) => {
      const pos = state.bMarks[startLine] + state.tShift[startLine];
      const max = state.eMarks[startLine];
      const lineText = state.src.slice(pos, max);

      const openTag = `[${ruleName}]`;
      if (!lineText.startsWith(openTag)) return false;

      let contentLines = [];
      let currentLine = startLine;
      let foundClose = false;

      const closeTag = `[/${ruleName}]`;
      if (lineText.includes(closeTag)) {
        const content = lineText.replace(openTag, '').replace(closeTag, '');
        contentLines.push(content);
        foundClose = true;
        currentLine = startLine + 1;
      } else {
        const firstContent = lineText.replace(openTag, '');
        if (firstContent) contentLines.push(firstContent);

        for (
          currentLine = startLine + 1;
          currentLine < endLine;
          currentLine += 1
        ) {
          const nextPos = state.bMarks[currentLine] + state.tShift[currentLine];
          const nextMax = state.eMarks[currentLine];
          const nextLine = state.src.slice(nextPos, nextMax);

          if (nextLine.includes(closeTag)) {
            const lastContent = nextLine.replace(closeTag, '');
            if (lastContent) contentLines.push(lastContent);
            foundClose = true;
            currentLine += 1;
            break;
          }
          contentLines.push(nextLine);
        }
      }

      if (!foundClose) return false;
      if (silent) return true;

      const styles = [`text-align: ${alignment}`, ...extraStyles];
      const token = state.push('paragraph_open', 'p', 1);
      token.attrs = [['style', styles.join('; ')]];
      token.map = [startLine, currentLine];

      const contentToken = state.push('inline', '', 0);
      contentToken.content = contentLines.join('\n');
      contentToken.map = [startLine, currentLine];
      contentToken.children = [];

      state.push('paragraph_close', 'p', -1);

      state.line = currentLine;
      return true;
    }
  );
}

function createListDirectionRule(md) {
  md.core.ruler.push('list_direction', state => {
    const { tokens } = state;
    let i = 0;
    while (i < tokens.length) {
      const token = tokens[i];
      if (token.type === 'inline' && token.children?.length === 1) {
        const text = token.children[0].content;
        const paragraphOpenIdx = i - 1;
        const paragraphCloseIdx = i + 1;
        const isParagraphBlock =
          tokens[paragraphOpenIdx]?.type === 'paragraph_open' &&
          tokens[paragraphCloseIdx]?.type === 'paragraph_close';

        if (!isParagraphBlock) {
          i += 1;
          continue;
        }

        if (text === '[bulletListRTL]' || text === '[orderedListRTL]') {
          const listOpenType =
            text === '[bulletListRTL]' ? 'bullet_list_open' : 'ordered_list_open';
          let nextIdx = paragraphCloseIdx + 1;
          while (nextIdx < tokens.length && tokens[nextIdx].type !== listOpenType) {
            nextIdx += 1;
          }
          if (nextIdx < tokens.length) {
            tokens[nextIdx].attrSet('dir', 'rtl');
            tokens.splice(paragraphOpenIdx, 3);
            i = paragraphOpenIdx;
            continue;
          }
        } else if (text === '[/bulletListRTL]' || text === '[/orderedListRTL]') {
          tokens.splice(paragraphOpenIdx, 3);
          i = paragraphOpenIdx;
          continue;
        }
      }
      i += 1;
    }
  });
}

const CUSTOM_TAG_NAMES = ['fontSize', 'fontFamily', 'textColor'];
const BLANK_LINE_PLACEHOLDER = '​';

// A blank line always ends a markdown-it paragraph, and each paragraph gets
// its own inline-parsing scope (state.delimiters is per-paragraph). If a
// [fontSize=]/[fontFamily=]/[textColor=] span has a blank line in the
// middle, its opening and closing tags end up in different paragraphs and
// can never be paired back up by the rules above. Swap those blank lines
// for an invisible placeholder so the whole span stays one paragraph; the
// placeholder still renders as a visual blank line via the <br> either side
// of it (breaks: true).
export function preserveBlankLinesInCustomTags(text) {
  return CUSTOM_TAG_NAMES.reduce((acc, tag) => {
    const regex = new RegExp(`\\[${tag}=[^\\]]*\\].*?\\[/${tag}\\]`, 'gs');
    return acc.replace(regex, match =>
      match
        .split('\n')
        .map(line => (line.trim() === '' ? BLANK_LINE_PLACEHOLDER : line))
        .join('\n')
    );
  }, text);
}

const CUSTOM_OPEN_TAG = '(?:\\[(?:fontSize|fontFamily|textColor)=[^\\]]*\\])';
const CUSTOM_CLOSE_TAG = '(?:\\[/(?:fontSize|fontFamily|textColor)\\])';

// markdown-it's link rule finds the closing `]` of a link label via
// parseLinkLabel(state, pos, disableNested=true): it walks the label using
// skipToken(), which runs every inline rule (including the ones above) to
// skip past recognised tokens. If our tokenize() swallows a `[` as part of
// a bigger match (e.g. the whole "[fontSize=18]"), parseLinkLabel sees a
// "[" that wasn't consumed as a single plain character and - because
// disableNested is true for link labels - aborts the entire link parse.
// So "[[fontSize=18]text[/fontSize]](url)" can never become a real <a>;
// the brackets stay literal and only the bare URL gets auto-linked.
// Work around it by hoisting a run of our tags that sits directly against
// the label's outer brackets to outside the link before markdown-it ever
// sees it - the link label is then plain text and parses normally, with
// our tags now wrapping the whole <a> instead of sitting inside it.
export function unwrapCustomTagsFromLinkLabel(text) {
  const regex = new RegExp(
    `\\[(${CUSTOM_OPEN_TAG}+)([\\s\\S]*?)(${CUSTOM_CLOSE_TAG}+)\\]\\(([^()\\s]+)\\)`,
    'g'
  );
  return text.replace(
    regex,
    (match, prefix, body, suffix, url) => `${prefix}[${body}](${url})${suffix}`
  );
}

export default md => {
  createFontSizeRule(md);
  createFontFamilyRule(md);
  createTextColorRule(md);
  createListDirectionRule(md);

  ['alignLeft', 'alignRight', 'alignCenter', 'alignJustify'].forEach(
    ruleName => {
      createAlignmentRule(md, ruleName);
    }
  );

  createAlignmentRule(md, 'alignRightRTL', ['direction: rtl']);
  createAlignmentRule(md, 'alignLeftLTR', ['direction: ltr']);
};
